import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import { Box, Button, Menu, MenuItem, Popper, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { formatDate } from "src/utils";
import { colors } from "../ui/colors";
import { JobLayout } from "@components/jobs/jobs-list/jobs-layout/Job.layout";
import { BaseModalComponent } from "@components/common/BaseModalComponent";

interface MoreJobsPopoverProps {
  jobs: any[];
  timeZone: string;
  closeKey: number;
  onSelectJob: (job: any) => void;
  onOpenChange?: (open: boolean) => void;
  forceOpen?: boolean;
  highlightedJobId?: string | number | null;
  disableMenu?: boolean;
  variant?: "default" | "advanced";
  disableJobOpen?: boolean;
  showCalendarInModal?: boolean;
}

const getJobColors = (job: any) => {
  const isResidential = job.type === "residential";

  return {
    background: isResidential ? "var(--fx-colors-warning-100, #FFEDD5)" : "#bae5fb",
    hoverBackground: isResidential ? "#ffd79a" : "#9ed8f5",
    text: isResidential ? "#b85b00" : "#0b5f89",
    border: isResidential ? "rgba(255, 137, 18, 0.35)" : "rgba(33, 150, 243, 0.35)",
  };
};

const jobMatchesId = (job: any, targetId?: string | number | null) => {
  if (!targetId) return false;

  const target = String(targetId);
  const candidates = [
    job?.id,
    job?.jobId,
    job?.serviceOrderId,
    job?.eventUid,
    job?.uid,
    job?.event?.id,
    job?.event?.uid,
    job?.visitId,
    job?.visit?.id,
  ];

  return candidates.some((candidate) => candidate !== undefined && candidate !== null && String(candidate) === target);
};

export const MoreJobsPopover = ({
  jobs,
  timeZone,
  closeKey,
  onSelectJob,
  onOpenChange,
  forceOpen = false,
  highlightedJobId = null,
  disableMenu = false,
  variant = "default",
  disableJobOpen = false,
  showCalendarInModal = true,
}: MoreJobsPopoverProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedJob, setSelectedJob] = useState<any | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const modalTriggerRef = useRef<HTMLButtonElement | null>(null);
  const onOpenChangeRef = useRef(onOpenChange);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disableMenu) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    handleClose();
  }, [closeKey]);

  useEffect(() => {
    if (!forceOpen) return;
    if (!triggerRef.current) return;

    setAnchorEl(triggerRef.current);
  }, [forceOpen]);

  const open = Boolean(anchorEl);
  const isAdvanced = variant === "advanced";

  useEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  }, [onOpenChange]);

  useEffect(() => {
    onOpenChangeRef.current?.(open);
  }, [open]);

  const selectedEventUid = selectedJob?.eventUid || selectedJob?.event?.uid || selectedJob?.uid || selectedJob?.id;
  const selectedJobId = selectedJob?.jobId || selectedJob?.serviceOrderId || selectedJob?.id;
  const selectedJobForLayout = selectedJob
    ? {
        ...selectedJob,
        id: selectedJobId,
      }
    : null;

  const handleSelectJob = (job: any) => {
    onSelectJob(job);
    setSelectedJob(job);
    handleClose();

    if (disableJobOpen) return;

    window.requestAnimationFrame(() => {
      modalTriggerRef.current?.click();
    });
  };

  return (
    <>
      <Button
        ref={triggerRef}
        size="small"
        onMouseDown={(event) => event.stopPropagation()}
        onMouseEnter={isAdvanced ? undefined : handleOpen}
        onClick={handleOpen}
        sx={{
          minWidth: 0,
          height: 26,
          px: 1.15,
          borderRadius: "999px",
          border: `1px solid ${colors.gray[200]}`,
          bgcolor: colors.gray[50],
          color: colors.gray[700],
          fontSize: "0.74rem",
          fontWeight: 700,
          textTransform: "none",
          whiteSpace: "nowrap",
          gap: 0.25,
          "&:hover": { bgcolor: colors.gray[100] },
        }}
      >
        +{jobs.length}
        <KeyboardArrowDownIcon sx={{ fontSize: 15 }} />
      </Button>

      <BaseModalComponent
        modalLabel={`Job ${selectedJobId || ""}`}
        modalKey={`Timeline-More-Event-Stepper-${selectedJobId || "job"}-${selectedEventUid || "event"}`}
        elementTrigger={<button ref={modalTriggerRef} type="button" style={{ display: "none" }} />}
      >
        {selectedJobForLayout ? (
          <JobLayout job={selectedJobForLayout} currentEventUid={String(selectedEventUid)} showCalendar={showCalendarInModal} />
        ) : (
          <Box />
        )}
      </BaseModalComponent>

      {isAdvanced && !disableMenu && (
        <Popper
          open={open}
          anchorEl={anchorEl}
          placement="bottom-start"
          modifiers={[
            {
              name: "offset",
              options: {
                offset: [0, 42],
              },
            },
          ]}
          sx={{ zIndex: 999999999 }}
        >
          <Box>
            {jobs.map((job) => {
              const jobColors = getJobColors(job);
              const eventUid = job.eventUid || job.event?.uid || job.uid || job.id;
              const jobId = job.jobId || job.serviceOrderId || job.id;
              const isHighlighted = jobMatchesId(job, highlightedJobId);

              return (
                <Box
                  key={eventUid || job.id}
                  role="button"
                  tabIndex={0}
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={() => handleSelectJob(job)}
                  sx={{
                    mt: 1,
                    px: 0.9,
                    py: 0.35,
                    width: 150,
                    maxWidth: 150,
                    borderRadius: "4px",
                    border: `1px solid ${isHighlighted ? jobColors.text : jobColors.border}`,
                    bgcolor: isHighlighted ? jobColors.hoverBackground : jobColors.background,
                    color: jobColors.text,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    height: 38,
                    overflow: "hidden",
                    boxShadow: isHighlighted ? "0px 8px 22px rgba(0,0,0,0.16)" : "0px 2px 4px rgba(0,0,0,0.04)",
                    cursor: disableJobOpen ? "default" : "pointer",
                    position: "relative",
                    zIndex: isHighlighted ? 2 : 1,
                    transition: "background-color 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
                    "&:hover": {
                      bgcolor: jobColors.hoverBackground,
                      borderColor: jobColors.text,
                      boxShadow: "0px 8px 22px rgba(0,0,0,0.16)",
                    },
                  }}
                >
                  <Typography
                    noWrap
                    sx={{
                      position: "absolute",
                      top: 3,
                      right: 6,
                      fontSize: "0.58rem",
                      fontWeight: 800,
                      lineHeight: 1,
                      color: jobColors.text,
                    }}
                  >
                    #{jobId}
                  </Typography>

                  <Typography
                    noWrap
                    sx={{
                      fontSize: "0.62rem",
                      fontWeight: 800,
                      lineHeight: 1.1,
                      color: jobColors.text,
                      maxWidth: "calc(100% - 42px)",
                    }}
                  >
                    {`${formatDate(job.startDate, timeZone, "HH:mm")} - ${formatDate(job.endDate, timeZone, "HH:mm")}`}
                  </Typography>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, width: "100%", maxWidth: "100%", minWidth: 0 }}>
                    <WorkOutlineOutlinedIcon sx={{ fontSize: 15, color: jobColors.text }} />
                    <Typography
                      noWrap
                      sx={{
                        mt: 0.25,
                        fontSize: "0.72rem",
                        fontWeight: 800,
                        lineHeight: 1.15,
                        color: "var(--fx-surface-textsecondary, #64748B)",
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      {job.alias || (!/^service\s*#/i.test(job.title || "") ? job.title : "") || `Job #${jobId}`}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Popper>
      )}

      {!disableMenu && !isAdvanced && (
        <Menu
          disablePortal
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          sx={{
            zIndex: 999999999,
            "& .MuiPaper-root": {
              mt: 5,
              minWidth: 180,
              bgcolor: "transparent",
              border: "none",
              boxShadow: "none",
              overflow: "visible",
            },
            "& .MuiList-root": {
              p: 0,
              bgcolor: "transparent",
            },
            "& .MuiMenuItem-root": {
              fontSize: "0.9rem",
              fontWeight: 500,
              color: colors.gray[800],
            },
          }}
        >
          {jobs.map((job) => {
            const jobColors = getJobColors(job);
            const eventUid = job.eventUid || job.event?.uid || job.uid || job.id;
            const jobId = job.jobId || job.serviceOrderId || job.id;
            const isHighlighted = jobMatchesId(job, highlightedJobId);

            return (
              <MenuItem
                key={eventUid || job.id}
                onMouseDown={(event) => event.stopPropagation()}
                onClick={() => handleSelectJob(job)}
                sx={{
                  ml: 0,
                  mt: 1,
                  px: 0.9,
                  py: 0.35,
                  width: 180,
                  maxWidth: 180,
                  borderRadius: "4px",
                  border: `1px solid ${isHighlighted ? jobColors.text : jobColors.border}`,
                  bgcolor: isHighlighted ? `${jobColors.hoverBackground} !important` : jobColors.background,
                  color: jobColors.text,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  height: 38,
                  overflow: "hidden",
                  boxShadow: isHighlighted ? "0px 8px 22px rgba(0,0,0,0.16)" : "0px 2px 4px rgba(0,0,0,0.04)",
                  cursor: "pointer",
                  position: "relative",
                  zIndex: isHighlighted ? 2 : 1,
                  transition: "background-color 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
                  "&:hover": {
                    bgcolor: `${jobColors.hoverBackground} !important`,
                    color: `${jobColors.text} !important`,
                    borderColor: jobColors.text,
                    boxShadow: "0px 8px 22px rgba(0,0,0,0.16)",
                  },
                  "&.Mui-focusVisible, &.Mui-selected, &.Mui-selected:hover": {
                    bgcolor: `${jobColors.background} !important`,
                    color: `${jobColors.text} !important`,
                  },
                }}
              >
                <Typography
                  noWrap
                  sx={{
                    position: "absolute",
                    top: 3,
                    right: 6,
                    fontSize: "0.58rem",
                    fontWeight: 800,
                    lineHeight: 1,
                    color: jobColors.text,
                  }}
                >
                  #{jobId}
                </Typography>

                <Typography
                  noWrap
                  sx={{
                    fontSize: "0.62rem",
                    fontWeight: 800,
                    lineHeight: 1.1,
                    color: jobColors.text,
                    maxWidth: "calc(100% - 42px)",
                  }}
                >
                  {`${formatDate(job.startDate, timeZone, "HH:mm")} - ${formatDate(job.endDate, timeZone, "HH:mm")}`}
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, width: "100%", maxWidth: "100%", minWidth: 0 }}>
                  <WorkOutlineOutlinedIcon sx={{ fontSize: 15, color: jobColors.text }} />
                  <Typography
                    noWrap
                    sx={{
                      mt: 0.25,
                      fontSize: "0.72rem",
                      fontWeight: 800,
                      lineHeight: 1.15,
                      color: "var(--fx-surface-textsecondary, #64748B)",
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    {job.alias || (!/^service\s*#/i.test(job.title || "") ? job.title : "") || `Job #${jobId}`}
                  </Typography>
                </Box>
              </MenuItem>
            );
          })}
        </Menu>
      )}
    </>
  );
};
